import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { brand, text } from '../../theme/colors';

/**
 * The heart on a listing. A logged-out visitor is never left tapping a dead
 * control: the button stays live and calls `onRequireLogin` so the page can send
 * them to sign up and save the home for them when they come back.
 *
 * `disabled` means "not signed in", not "inert" — hence no MUI `disabled` prop.
 */
export default function SaveButton({
  saved = false, disabled = false, onToggle, onRequireLogin, withLabel = false, busy = false, sx = {},
}) {
  const label = saved ? 'Remove from saved homes' : 'Save this home';
  const handle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) { if (onRequireLogin) onRequireLogin(); return; }
    if (onToggle) onToggle();
  };

  const icon = saved
    ? <Favorite data-testid="heart-filled" sx={{ color: brand.secondary }} fontSize="small" />
    : <FavoriteBorder data-testid="heart-outline" sx={{ color: withLabel ? brand.primary : text.secondary }} fontSize="small" />;

  if (withLabel) {
    return (
      <Button
        fullWidth
        variant={saved ? 'contained' : 'outlined'}
        startIcon={icon}
        onClick={handle}
        aria-label={label}
        aria-pressed={saved}
        disabled={busy}
        sx={{
          textTransform: 'none', fontWeight: 600, borderRadius: 2,
          ...(saved ? { bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } } : { borderColor: brand.primary, color: brand.primary }),
          ...sx,
        }}
      >
        {saved ? 'Saved' : 'Save this home'}
      </Button>
    );
  }

  return (
    <Tooltip title={label}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          onClick={handle}
          aria-label={label}
          aria-pressed={saved}
          sx={{
            bgcolor: 'rgba(255,255,255,0.92)',
            '&:hover': { bgcolor: '#fff' },
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            ...sx,
          }}
        >
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  );
}
